/**
 * Deletion Banner (Story 0.8 - GDPR)
 *
 * Yellow warning banner shown at the top of the dashboard when the user's
 * account is pending deletion. Not a modal — user can still use the app.
 *
 * AC: #6, #16
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@provenleap/ui";
import { apiPost } from "../../lib/apiClient";

interface DeletionBannerProps {
  deletionDate: string;
}

export function DeletionBanner({ deletionDate }: DeletionBannerProps) {
  const queryClient = useQueryClient();

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const response = await apiPost("/api/account/cancel-deletion");
      if (!response.ok) {
        const errorBody = await response.json();
        throw new Error(
          (errorBody as { detail?: string }).detail ||
            "Failed to cancel deletion"
        );
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-status"] });
    },
  });

  return (
    <div
      className="bg-amber-50 border-b border-amber-200 px-4 py-3 dark:bg-amber-950/20 dark:border-amber-800"
      role="alert"
      data-testid="deletion-banner"
    >
      <div className="flex items-center justify-between max-w-[1200px] mx-auto">
        <p className="text-sm text-amber-800 dark:text-amber-200">
          Your account is scheduled for deletion on{" "}
          <strong>{deletionDate}</strong>.
        </p>
        <Button
          size="sm"
          variant="outline"
          className="ml-4 shrink-0 border-amber-600 text-amber-800 hover:bg-amber-100 dark:border-amber-400 dark:text-amber-200 dark:hover:bg-amber-900/40"
          onClick={() => cancelMutation.mutate()}
          disabled={cancelMutation.isPending}
          data-testid="cancel-deletion-banner-btn"
        >
          {cancelMutation.isPending ? "Cancelling..." : "Cancel Deletion"}
        </Button>
      </div>

      {cancelMutation.isError && (
        <p className="text-sm text-red-600 mt-1 max-w-[1200px] mx-auto">
          {cancelMutation.error.message}
        </p>
      )}
    </div>
  );
}
